import { supabase } from '../lib/supabase';

const BUCKET = 'rfi-documents';
const ATTACHMENTS_BUCKET = 'rfi-attachments';

/**
 * Sanitize a filename for safe storage paths.
 */
const sanitizeFilename = (name) => {
  return name
    ?.normalize('NFD')
    ?.replace(/[\u0300-\u036f]/g, '')
    ?.toLowerCase()
    ?.replace(/\s+/g, '_')
    ?.replace(/[^a-z0-9_.]/g, '');
};

/**
 * Create a new RFI for a project.
 * Auto-generates rfi_number via DB function.
 */
export const createRFI = async ({ projectId, subject, profession, question, createdBy }) => {
  try {
    const { data: rfiNumberData, error: rfiNumberError } = await supabase?.rpc('generate_rfi_number', { p_project_id: projectId });
    if (rfiNumberError) throw rfiNumberError;

    const { data, error } = await supabase?.from('project_rfis')?.insert({
      project_id: projectId,
      rfi_number: rfiNumberData,
      subject,
      profession: profession || null,
      question: question || null,
      status: 'draft',
      created_by: createdBy || null,
    })?.select()?.single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('createRFI error:', error);
    return { data: null, error };
  }
};

/**
 * List all RFIs for a given project, ordered by creation date descending.
 */
export const listRFIsByProject = async (projectId) => {
  try {
    const { data, error } = await supabase?.from('project_rfis')?.select('*')?.eq('project_id', projectId)?.order('created_at', { ascending: false });
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('listRFIsByProject error:', error);
    return { data: null, error };
  }
};

/**
 * Fetch a single RFI by ID, including response photos with signed URLs.
 */
export const getRFIById = async (id) => {
  try {
    const { data, error } = await supabase
      ?.from('project_rfis')
      ?.select('*, response_photos:rfi_response_photos(*)')
      ?.eq('id', id)
      ?.single();
    if (error) throw error;

    // Generate signed URL for response PDF
    if (data?.response_pdf_path) {
      const { data: urlData } = await supabase?.storage
        ?.from(ATTACHMENTS_BUCKET)
        ?.createSignedUrl(data?.response_pdf_path, 3600);
      data.response_pdf_url = urlData?.signedUrl || null;
    }

    // Generate signed URLs for response photos
    if (data?.response_photos && data?.response_photos?.length > 0) {
      for (const photo of data?.response_photos) {
        const { data: urlData } = await supabase?.storage
          ?.from(ATTACHMENTS_BUCKET)
          ?.createSignedUrl(photo?.storage_path, 3600);
        photo.signed_url = urlData?.signedUrl || null;
      }
    }

    return { data, error: null };
  } catch (error) {
    console.error('getRFIById error:', error);
    return { data: null, error };
  }
};

/**
 * Update the status of an RFI (draft | sent_for_approval | in_review).
 * For 'answered' status, use submitQrtResponse instead.
 */
export const updateRFIStatus = async (rfiId, status) => {
  try {
    if (status === 'answered') {
      throw new Error('Use submitQrtResponse for answered status');
    }
    const { data, error } = await supabase?.from('project_rfis')?.update({ status })?.eq('id', rfiId)?.select()?.single();
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('updateRFIStatus error:', error);
    return { data: null, error };
  }
};

/**
 * Enregistre LA reponse d'un QRT — texte, PDF, photos — et pose le statut
 * 'answered'.
 *
 * Il n'y a jamais qu'UNE reponse : modifier remplace. Cette fonction sert donc
 * aux deux cas, premiere reponse et modification.
 *
 * Le PDF :
 *  - premiere reponse -> OBLIGATOIRE (garde ci-dessous, en plus de la modale) ;
 *  - modification     -> facultatif. Sans nouveau fichier, l'ancien est
 *    conserve et `response_pdf_path` n'est pas reecrit.
 *  - un nouveau PDF remplace l'ancien, qui est SUPPRIME du seau une fois la
 *    base mise a jour. Sans ca, chaque modification laissait un orphelin.
 */
export const submitQrtResponse = async (rfiId, projectId, orgId, { responseText, responsePdfFile, responsePhotoFiles = [] }) => {
  try {
    // 1. Etat actuel : le PDF de la QUESTION et celui de la REPONSE.
    const { data: existingRfi } = await supabase
      ?.from('project_rfis')
      ?.select('rfi_pdf_path, response_pdf_path')
      ?.eq('id', rfiId)
      ?.single();

    const previousResponsePdfPath = existingRfi?.response_pdf_path || null;

    // Le PDF reste obligatoire tant qu'il n'y en a aucun : la modale le bloque
    // deja, cette garde empeche qu'un autre appelant contourne la regle.
    if (!responsePdfFile && !previousResponsePdfPath) {
      throw new Error('submitQrtResponse: un PDF est obligatoire pour la premiere reponse');
    }

    // 2. Nouveau PDF ? On le televerse. Sinon on garde l'existant.
    let pdfPath = null;
    if (responsePdfFile) {
      const pdfFileName = `${rfiId}_response_${Date.now()}.pdf`;
      pdfPath = `${projectId}/${rfiId}/${pdfFileName}`;
      const { error: uploadError } = await supabase?.storage
        ?.from(ATTACHMENTS_BUCKET)
        ?.upload(pdfPath, responsePdfFile, { contentType: 'application/pdf' });
      if (uploadError) throw uploadError;
    }

    // 3. Delete old auto-generated RFI PDF if exists
    if (existingRfi?.rfi_pdf_path) {
      await supabase?.storage?.from(ATTACHMENTS_BUCKET)?.remove([existingRfi?.rfi_pdf_path]);
    }

    // 4. Update project_rfis
    const { data: userData } = await supabase?.auth?.getUser();
    const updatePayload = {
      status: 'answered',
      response_text: responseText,
      rfi_pdf_path: null,
      answered_at: new Date()?.toISOString(),
      answered_by: userData?.user?.id,
    };
    // `response_pdf_path` n'est reecrit QUE si un nouveau fichier est fourni.
    if (pdfPath) updatePayload.response_pdf_path = pdfPath;

    const { data: updatedRfi, error: updateError } = await supabase
      ?.from('project_rfis')
      ?.update(updatePayload)
      ?.eq('id', rfiId)
      ?.select()
      ?.single();
    if (updateError) throw updateError;

    // 4b. L'ancien PDF de reponse n'est supprime qu'APRES la mise a jour : si
    // elle echouait, la ligne pointerait sur un fichier detruit.
    if (pdfPath && previousResponsePdfPath && previousResponsePdfPath !== pdfPath) {
      await supabase?.storage?.from(ATTACHMENTS_BUCKET)?.remove([previousResponsePdfPath]);
    }

    // 5. Upload response photos in parallel batches of 4
    const photoUploads = [];
    for (let i = 0; i < responsePhotoFiles?.length; i += 4) {
      const batch = responsePhotoFiles?.slice(i, i + 4);
      const batchPromises = batch?.map(async (file) => {
        const photoName = `${Date.now()}_${crypto.randomUUID()}.jpg`;
        const photoPath = `${projectId}/${rfiId}/photos/${photoName}`;
        await supabase?.storage?.from(ATTACHMENTS_BUCKET)?.upload(photoPath, file, { contentType: file?.type });
        return {
          rfi_id: rfiId,
          storage_path: photoPath,
          organization_id: orgId,
          uploaded_by: userData?.user?.id,
        };
      });
      photoUploads?.push(...(await Promise.all(batchPromises)));
    }

    // 6. Batch insert photo rows
    if (photoUploads?.length > 0) {
      await supabase?.from('rfi_response_photos')?.insert(photoUploads);
    }

    return { data: updatedRfi, error: null };
  } catch (error) {
    console.error('submitQrtResponse error:', error);
    return { data: null, error };
  }
};

/**
 * Upload one or more images to the rfi-documents bucket.
 */
export const uploadRFIImages = async (projectId, rfiId, files) => {
  const results = [];
  const errors = [];

  for (const file of files) {
    if (!['image/jpeg', 'image/png']?.includes(file?.type)) {
      errors?.push({ file: file?.name, error: 'Only JPEG and PNG images are allowed.' });
      continue;
    }
    const safeName = sanitizeFilename(file?.name);
    const filePath = `${projectId}/${rfiId}/${safeName}`;
    const { data, error } = await supabase?.storage?.from(BUCKET)?.upload(filePath, file, { upsert: true, contentType: file?.type });
    if (error) {
      errors?.push({ file: file?.name, error: error?.message });
    } else {
      results?.push({ path: filePath, data });
    }
  }

  return { data: results, errors };
};

/**
 * Delete a single image from the rfi-documents bucket by its storage path.
 * Sous RLS, un retrait refuse ne renvoie pas d'erreur, seulement zero objet :
 * on le traite comme un echec.
 */
export const deleteRFIImage = async (storagePath) => {
  try {
    const { data, error } = await supabase?.storage?.from(BUCKET)?.remove([storagePath]);
    if (error) throw error;
    if (!data?.length) throw new Error('Image not deleted');
    return { error: null };
  } catch (error) {
    console.error('deleteRFIImage error:', error);
    return { error };
  }
};

/**
 * Count the question photos of several RFIs (rfi-documents bucket), without
 * signing any URL. Returns { [rfiId]: count } ; null when the listing failed.
 */
export const countRFIImages = async (projectId, rfiIds = []) => {
  const entries = await Promise.all((rfiIds || [])?.map(async (rfiId) => {
    const { data, error } = await supabase?.storage?.from(BUCKET)?.list(`${projectId}/${rfiId}`, { limit: 100, offset: 0 });
    if (error) return [rfiId, null];
    // Le Storage peut renvoyer un fichier de remplissage (.emptyFolderPlaceholder).
    return [rfiId, (data || [])?.filter((f) => f?.name && !f?.name?.startsWith('.'))?.length];
  }));
  return Object.fromEntries(entries);
};

/**
 * List all images stored for a specific RFI. Returns signed URLs valid for 1 hour.
 */
export const listRFIImages = async (projectId, rfiId) => {
  try {
    const prefix = `${projectId}/${rfiId}`;
    const { data: files, error: listError } = await supabase?.storage?.from(BUCKET)?.list(prefix, { limit: 100, offset: 0 });
    if (listError) throw listError;
    if (!files || files?.length === 0) return { data: [], error: null };

    const paths = files?.map((f) => `${prefix}/${f?.name}`);
    const { data: signedUrls, error: signError } = await supabase?.storage?.from(BUCKET)?.createSignedUrls(paths, 3600);
    if (signError) throw signError;

    const data = (signedUrls || [])?.map((item, idx) => ({
      name: files?.[idx]?.name,
      path: item?.path,
      signedUrl: item?.signedUrl,
    }));

    return { data, error: null };
  } catch (error) {
    console.error('listRFIImages error:', error);
    return { data: null, error };
  }
};

/**
 * Update basic fields of an RFI (subject, profession, question).
 * Status changes use updateRFIStatus or submitQrtResponse instead.
 */
export const updateRFI = async (rfiId, updates) => {
  try {
    const allowed = {};
    if (updates.subject !== undefined) allowed.subject = updates.subject;
    if (updates.profession !== undefined) allowed.profession = updates.profession;
    if (updates.question !== undefined) allowed.question = updates.question;

    const { data, error } = await supabase
      ?.from('project_rfis')
      ?.update(allowed)
      ?.eq('id', rfiId)
      ?.select()
      ?.single();
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('updateRFI error:', error);
    return { data: null, error };
  }
};

// ─── Spec-named aliases ───────────────────────────────────────
// These match the exact method names specified in the build plan.

/**
 * Alias for getRFIById — fetch single RFI with response photos + signed URLs.
 */
export const getById = getRFIById;

/**
 * Alias for updateRFIStatus — update status for non-answered transitions.
 */
export const updateStatus = updateRFIStatus;

/**
 * Alias for updateRFI — update subject / profession / question.
 */
export const update = updateRFI;
export const deleteImage = deleteRFIImage;
